import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import {
  Users,
  MapPin,
  ArrowLeft,
  Check,
  X,
  Trash2,
  Home,
  Building2,
  LandPlot,
  LayoutDashboard,
  RefreshCw,
  LogOut,
  Activity,
  Clock,
  Download,
  FileSpreadsheet,
  TrendingDown,
  Eye,
  FileText,
  BarChart3,
} from 'lucide-react';
import { BarChart, Bar, CartesianGrid, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { API, resolveApiAssetUrl } from '@/lib/api';

const typeLabels = {
  haus: 'Haus',
  wohnung: 'Wohnung',
  grundstueck: 'Grundstück',
};

const eventTone = {
  online: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  offline: 'bg-amber-100 text-amber-800 border-amber-200',
  manual_offline: 'bg-amber-100 text-amber-800 border-amber-200',
  hidden: 'bg-stone-200 text-stone-800 border-stone-300',
  feedback_requested: 'bg-blue-100 text-blue-800 border-blue-200',
  feedback_submitted: 'bg-slate-900 text-white border-slate-900',
  price_change: 'bg-rose-100 text-rose-800 border-rose-200',
};

function formatDateTime(value) {
  if (!value) {
    return '-';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return new Intl.DateTimeFormat('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

function formatPrice(value) {
  if (value === null || value === undefined || value === '') {
    return '-';
  }
  const numeric = Number(value);
  if (Number.isNaN(numeric)) {
    return String(value);
  }
  return `${numeric.toLocaleString('de-DE')} EUR`;
}

function sortDateValue(value) {
  const date = new Date(value || 0);
  return Number.isNaN(date.getTime()) ? 0 : date.getTime();
}

function formatNumber(value) {
  return Number(value || 0).toLocaleString('de-DE');
}

function formatMonths(days) {
  if (days === null || days === undefined) {
    return '-';
  }
  return `${days} Tage / ${Math.round((days / 30.44) * 10) / 10} Monate`;
}

function normalizeBrokerKey(value) {
  let normalizedValue = value || '';
  try {
    normalizedValue = decodeURIComponent(escape(normalizedValue));
  } catch (error) {
    // Keep the original value when it is already valid Unicode.
  }

  return normalizedValue
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .replace(/\bra ther\b/g, 'rather')
    .replace(/\bma sel\b/g, 'masel')
    .replace(/\bo si\b/g, 'osi');
}

function canEditJointBusiness(property) {
  const brokerKey = normalizeBrokerKey(property?.responsible_broker);
  return (
    brokerKey.includes('christian raether')
    || brokerKey.includes('christian rather')
    || brokerKey.includes('felix maesel')
    || brokerKey.includes('felix masel')
  );
}

function canUserToggleJointBusiness(user, property) {
  if (!canEditJointBusiness(property)) {
    return false;
  }
  if (user?.admin_permissions !== 'read_only') {
    return true;
  }

  const userKey = normalizeBrokerKey(`${user?.name || ''} ${user?.email || ''}`);
  const brokerKey = normalizeBrokerKey(property?.responsible_broker);
  return userKey.includes('felix') && brokerKey.includes('felix');
}

function isJointBusiness(property) {
  const value = property?.joint_business_upload_answer || property?.latest_feedback?.joint_business || property?.joint_business;
  return value === 'ja' || value === true;
}

const brokerColors = ['#0f766e', '#b45309', '#334155', '#be123c', '#2563eb', '#7c3aed'];

function formatFeedbackSummary(feedback) {
  if (!feedback) {
    return '';
  }

  const details = [feedback.reason_label];
  if (feedback.deal_type_label) {
    details.push(feedback.deal_type_label);
  }
  if (feedback.deal_amount) {
    details.push(`${feedback.deal_type === 'rent' ? 'Provision absolut' : 'Verkauft'}: ${formatPrice(feedback.deal_amount)}`);
  }
  if (feedback.deal_type !== 'rent' && feedback.net_commission_percent) {
    details.push(`Nettoprovision ${feedback.net_commission_percent} %`);
  }
  if (feedback.deal_type !== 'rent' && feedback.tipgeber_commission_amount) {
    details.push(`Tippgeber ${formatPrice(feedback.tipgeber_commission_amount)}`);
  }
  if (feedback.strategic_new_id) {
    details.push(`Neue ID: ${feedback.strategic_new_id}`);
  }
  if (feedback.lost_reason) {
    details.push(feedback.lost_reason);
  }
  if (feedback.paused_note) {
    details.push(feedback.paused_note);
  }
  if (feedback.lead_source) {
    details.push(`Herkunft: ${feedback.lead_source}`);
  }
  return details.filter(Boolean).join(' · ');
}

export default function AdminPanel({ user }) {
  const navigate = useNavigate();
  const [brokers, setBrokers] = useState([]);
  const [brokerDirectory, setBrokerDirectory] = useState([]);
  const [properties, setProperties] = useState([]);
  const [immoStatus, setImmoStatus] = useState({ stats: {}, properties: [], events: [], workbook: {} });
  const [stats, setStats] = useState({ total: 0, houses: 0, apartments: 0, properties: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [isSyncingStore, setIsSyncingStore] = useState(false);
  const [isSavingBroker, setIsSavingBroker] = useState(false);
  const [savingJointBusinessId, setSavingJointBusinessId] = useState(null);
  const [newBrokerName, setNewBrokerName] = useState('');
  const [editingBrokers, setEditingBrokers] = useState({});
  const [deleteDialog, setDeleteDialog] = useState({ open: false, type: '', id: '', name: '' });
  const [statusFilter, setStatusFilter] = useState('all');
  const [feedbackFilter, setFeedbackFilter] = useState('all');
  const [statusSort, setStatusSort] = useState('latest');
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [selectedBrokerStats, setSelectedBrokerStats] = useState(null);
  const [selectedBrokerPropertyFilter, setSelectedBrokerPropertyFilter] = useState('all');
  const [brokerYear, setBrokerYear] = useState(String(new Date().getFullYear()));
  const canWriteAdmin = user?.admin_permissions !== 'read_only';

  const fetchData = useCallback(async () => {
    try {
      const [brokersRes, brokerDirectoryRes, propertiesRes, statsRes, immoStatusRes] = await Promise.all([
        axios.get(`${API}/admin/brokers`, { withCredentials: true }),
        axios.get(`${API}/admin/broker-directory`, { withCredentials: true }),
        axios.get(`${API}/properties`, { withCredentials: true }),
        axios.get(`${API}/stats`, { withCredentials: true }),
        axios.get(`${API}/admin/immostatus`, { withCredentials: true }),
      ]);

      setBrokers(brokersRes.data);
      setBrokerDirectory(brokerDirectoryRes.data);
      setEditingBrokers(
        Object.fromEntries(
          brokerDirectoryRes.data.map((broker) => [
            broker.broker_id,
            { name: broker.name, is_active: broker.is_active },
          ])
        )
      );
      setProperties(propertiesRes.data);
      setStats(statsRes.data);
      setImmoStatus(immoStatusRes.data);
    } catch (error) {
      console.error('Fehler beim Laden der Daten:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        toast.error('Bitte erneut anmelden');
        navigate('/login?redirect=%2Fadmin');
        return;
      }
      toast.error('Admin-Daten konnten nicht geladen werden');
    } finally {
      setIsLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleApproval = async (userId, isApproved) => {
    try {
      await axios.post(
        `${API}/admin/brokers/approve`,
        { user_id: userId, is_approved: isApproved },
        { withCredentials: true }
      );
      toast.success(isApproved ? 'Benutzer freigegeben' : 'Benutzer gesperrt');
      fetchData();
    } catch {
      toast.error('Fehler beim Aktualisieren des Benutzerstatus');
    }
  };

  const handleDeleteBrokerUser = async () => {
    try {
      await axios.delete(`${API}/admin/brokers/${deleteDialog.id}`, { withCredentials: true });
      toast.success('Benutzer gelöscht');
      setDeleteDialog({ open: false, type: '', id: '', name: '' });
      fetchData();
    } catch {
      toast.error('Fehler beim Löschen des Benutzers');
    }
  };

  const handleDeleteProperty = async () => {
    try {
      await axios.delete(`${API}/properties/${deleteDialog.id}`, { withCredentials: true });
      toast.success('Immobilie gelöscht');
      setDeleteDialog({ open: false, type: '', id: '', name: '' });
      fetchData();
    } catch {
      toast.error('Fehler beim Löschen der Immobilie');
    }
  };

  const handleDeleteBrokerDirectory = async () => {
    try {
      await axios.delete(`${API}/admin/broker-directory/${deleteDialog.id}`, { withCredentials: true });
      toast.success('Makler gelöscht');
      setDeleteDialog({ open: false, type: '', id: '', name: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Löschen des Maklers');
    }
  };

  const handleAddBroker = async () => {
    if (!newBrokerName.trim()) {
      toast.error('Bitte einen Maklernamen eingeben');
      return;
    }

    setIsSavingBroker(true);
    try {
      await axios.post(
        `${API}/admin/broker-directory`,
        { name: newBrokerName },
        { withCredentials: true }
      );
      toast.success('Makler angelegt');
      setNewBrokerName('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Makler konnte nicht angelegt werden');
    } finally {
      setIsSavingBroker(false);
    }
  };

  const handleStoreSync = async () => {
    setIsSyncingStore(true);
    try {
      const response = await axios.post(`${API}/admin/sync-immonex-store`, {}, { withCredentials: true });
      toast.success(response.data.message);
      if (response.data.new_brokers?.length) {
        toast.info(`Neue Makler bitte prüfen: ${response.data.new_brokers.join(', ')}`);
      }
      await fetchData();
    } catch (error) {
      console.error('MR-properties-Store-Sync fehlgeschlagen:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Synchronisieren des MR-properties-Ordners');
    } finally {
      setIsSyncingStore(false);
    }
  };

  const handleBrokerFieldChange = (brokerId, field, value) => {
    setEditingBrokers((current) => ({
      ...current,
      [brokerId]: {
        ...current[brokerId],
        [field]: value,
      },
    }));
  };

  const handleSaveBroker = async (brokerId) => {
    const payload = editingBrokers[brokerId];
    if (!payload?.name?.trim()) {
      toast.error('Bitte einen Maklernamen eingeben');
      return;
    }

    setIsSavingBroker(true);
    try {
      await axios.put(`${API}/admin/broker-directory/${brokerId}`, payload, { withCredentials: true });
      toast.success('Makler aktualisiert');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Makler konnte nicht gespeichert werden');
    } finally {
      setIsSavingBroker(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } finally {
      toast.success('Abgemeldet');
      navigate('/login?logout=1', { replace: true });
    }
  };

  const handleDownloadImmoStatus = () => {
    window.location.href = `${API}/admin/immostatus/download`;
  };

  const handleJointBusinessChange = async (property, checked) => {
    if (!canUserToggleJointBusiness(user, property) || !property?.property_id) {
      return;
    }

    setSavingJointBusinessId(property.property_id);
    try {
      const response = await axios.patch(
        `${API}/admin/properties/${property.property_id}/joint-business`,
        { joint_business: checked },
        { withCredentials: true }
      );
      const update = {
        joint_business_upload_status: response.data.joint_business_upload_status,
        joint_business_upload_answer: response.data.joint_business_upload_answer,
        joint_business_upload_answered_at: response.data.joint_business_upload_answered_at,
      };

      setProperties((current) => current.map((item) => (
        item.property_id === property.property_id ? { ...item, ...update } : item
      )));
      setImmoStatus((current) => ({
        ...current,
        properties: (current.properties || []).map((item) => (
          item.property_id === property.property_id ? { ...item, ...update } : item
        )),
        broker_stats: current.broker_stats,
      }));
      await fetchData();
      toast.success(checked ? 'Gemeinschaftsgeschäft markiert' : 'Gemeinschaftsgeschäft entfernt');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gemeinschaftsgeschäft konnte nicht gespeichert werden');
    } finally {
      setSavingJointBusinessId(null);
    }
  };

  const immoStatusYears = useMemo(() => {
    const years = new Set([String(new Date().getFullYear())]);
    Object.values(immoStatus.broker_stats || {}).forEach((brokerYears) => {
      Object.keys(brokerYears || {}).forEach((year) => years.add(String(year)));
    });
    return [...years].sort((a, b) => Number(b) - Number(a));
  }, [immoStatus.broker_stats]);

  const filteredImmoStatusProperties = useMemo(() => {
    return [...(immoStatus.properties || [])]
      .filter((property) => {
        if (statusFilter !== 'all' && property.current_status !== statusFilter) {
          return false;
        }
        if (feedbackFilter === 'pending' && !property.feedback_pending) {
          return false;
        }
        if (feedbackFilter === 'answered' && !property.last_feedback_at) {
          return false;
        }
        if (feedbackFilter === 'none' && (property.feedback_pending || property.last_feedback_at)) {
          return false;
        }
        return true;
      })
      .sort((a, b) => {
        if (statusSort === 'marketing_days') {
          return (b.marketing_days || 0) - (a.marketing_days || 0);
        }
        if (statusSort === 'price_reductions') {
          return (b.price_reductions || 0) - (a.price_reductions || 0);
        }
        if (statusSort === 'offline') {
          return sortDateValue(b.last_offline_at) - sortDateValue(a.last_offline_at);
        }
        if (statusSort === 'feedback') {
          return sortDateValue(b.last_feedback_at) - sortDateValue(a.last_feedback_at);
        }
        return sortDateValue(b.events?.[0]?.occurred_at) - sortDateValue(a.events?.[0]?.occurred_at);
      });
  }, [feedbackFilter, immoStatus.properties, statusFilter, statusSort]);

  const selectedYearBrokerStats = useMemo(() => {
    const order = ['Christian Räther', 'Felix Mäsel', 'Dominik Mahnke', 'Mansur Anwar', 'Gabor Huszak', 'M&R'];
    return Object.entries(immoStatus.broker_stats || {})
      .map(([brokerName, years]) => ({
        brokerName,
        ...(years?.[brokerYear] || {
          year: brokerYear,
          active_properties: 0,
          offline_properties: 0,
          feedback_submissions: 0,
          feedback_pending: 0,
          property_volume: 0,
          realized_commission: 0,
          tipgeber_commission: 0,
          pienzenauer_commission: 0,
          avg_marketing_days: 0,
          types: {},
          active_types: {},
          archived_types: {},
          office_active_types: {},
          office_archived_types: {},
          feedback_reasons: {},
          properties: [],
        }),
      }))
      .sort((a, b) => {
        const ai = order.indexOf(a.brokerName);
        const bi = order.indexOf(b.brokerName);
        if (ai !== -1 || bi !== -1) {
          return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
        }
        return a.brokerName.localeCompare(b.brokerName);
      });
  }, [brokerYear, immoStatus.broker_stats]);

  const brokerVolumeData = selectedYearBrokerStats.map((broker) => ({
    name: broker.brokerName.split(' ').slice(-1)[0],
    volume: Math.round((broker.property_volume || 0) / 1000),
    active: broker.active_properties || 0,
    offline: broker.offline_properties || 0,
  }));

  const officeTypeData = selectedYearBrokerStats.reduce((offices, broker) => {
    Object.entries(broker.office_active_types || {}).forEach(([office, types]) => {
      const bucket = offices[office] || { active: {}, archived: {} };
      Object.entries(types || {}).forEach(([type, value]) => {
        bucket.active[type] = (bucket.active[type] || 0) + value;
      });
      offices[office] = bucket;
    });
    Object.entries(broker.office_archived_types || {}).forEach(([office, types]) => {
      const bucket = offices[office] || { active: {}, archived: {} };
      Object.entries(types || {}).forEach(([type, value]) => {
        bucket.archived[type] = (bucket.archived[type] || 0) + value;
      });
      offices[office] = bucket;
    });
    return offices;
  }, {});

  const selectedBrokerProperties = useMemo(() => {
    const list = selectedBrokerStats?.properties || [];
    if (selectedBrokerPropertyFilter === 'active') {
      return list.filter((property) => property.current_status === 'Online');
    }
    if (selectedBrokerPropertyFilter === 'archived') {
      return list.filter((property) => property.current_status !== 'Online');
    }
    return list;
  }, [selectedBrokerPropertyFilter, selectedBrokerStats]);

  useEffect(() => {
    if (!selectedBrokerStats) {
      return;
    }

    const refreshedBroker = selectedYearBrokerStats.find((broker) => broker.brokerName === selectedBrokerStats.brokerName);
    if (refreshedBroker && refreshedBroker !== selectedBrokerStats) {
      setSelectedBrokerStats(refreshedBroker);
    }
  }, [selectedBrokerStats, selectedYearBrokerStats]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-stone-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-100" data-testid="admin-panel">
      <header className="bg-slate-900 text-white">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                data-testid="back-to-dashboard-btn"
                variant="ghost"
                size="sm"
                onClick={() => navigate('/dashboard')}
                className="text-white hover:bg-slate-800"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Zurück zur Karte
              </Button>
              <div className="h-6 w-px bg-slate-700" />
              <h1 className="heading-md">Admin-Bereich</h1>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm text-stone-300">{user?.email}</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/kanban')}
                className="text-white hover:bg-slate-800"
              >
                <LayoutDashboard className="w-4 h-4 mr-2" />
                Kanban
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="text-white hover:bg-slate-800"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Abmelden
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-6 py-8">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-8">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <Card className="border-0 shadow-lg">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="overline mb-1">Gesamt</p>
                    <p className="text-3xl font-bold text-slate-900">{stats.total}</p>
                  </div>
                  <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center">
                    <MapPin className="w-6 h-6 text-emerald-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <Card className="border-0 shadow-lg">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="overline mb-1">Häuser</p>
                    <p className="text-3xl font-bold text-blue-600">{stats.houses}</p>
                  </div>
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                    <Home className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
            <Card className="border-0 shadow-lg">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="overline mb-1">Wohnungen</p>
                    <p className="text-3xl font-bold text-violet-600">{stats.apartments}</p>
                  </div>
                  <div className="w-12 h-12 bg-violet-100 rounded-full flex items-center justify-center">
                    <Building2 className="w-6 h-6 text-violet-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
            <Card className="border-0 shadow-lg">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="overline mb-1">Grundstücke</p>
                    <p className="text-3xl font-bold text-emerald-600">{stats.properties}</p>
                  </div>
                  <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center">
                    <LandPlot className="w-6 h-6 text-emerald-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}>
            <Card className="border-0 shadow-lg">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="overline mb-1">Status</p>
                    <p className="text-3xl font-bold text-amber-700">{immoStatus.stats?.events || 0}</p>
                  </div>
                  <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                    <Activity className="w-6 h-6 text-amber-700" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        <Tabs defaultValue="properties" className="space-y-6">
          <TabsList className="bg-white shadow-sm p-1 h-auto flex flex-wrap">
            <TabsTrigger value="properties" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white px-6 py-2" data-testid="properties-tab">
              <MapPin className="w-4 h-4 mr-2" />
              Immobilien ({properties.length})
            </TabsTrigger>
            <TabsTrigger value="upload" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white px-6 py-2" data-testid="upload-tab">
              <RefreshCw className="w-4 h-4 mr-2" />
              Datenstand
            </TabsTrigger>
            <TabsTrigger value="immostatus" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white px-6 py-2">
              <FileSpreadsheet className="w-4 h-4 mr-2" />
              MR ImmoSTATUS
            </TabsTrigger>
            <TabsTrigger value="brokerstats" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white px-6 py-2">
              <BarChart3 className="w-4 h-4 mr-2" />
              Makler-Statistik
            </TabsTrigger>
            <TabsTrigger value="directory" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white px-6 py-2">
              <Users className="w-4 h-4 mr-2" />
              Makler ({brokerDirectory.length})
            </TabsTrigger>
            <TabsTrigger value="users" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white px-6 py-2" data-testid="brokers-tab">
              <Users className="w-4 h-4 mr-2" />
              Benutzer ({brokers.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="properties">
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="heading-md">Immobilienverwaltung</CardTitle>
                    <CardDescription>Importierte Immobilien anzeigen und verwalten</CardDescription>
                  </div>
                  <Button variant="outline" size="sm" onClick={fetchData}>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Aktualisieren
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Titel</TableHead>
                      <TableHead>Typ</TableHead>
                      <TableHead>Standort</TableHead>
                      <TableHead>Preis</TableHead>
                      <TableHead>Objekt-Nr.</TableHead>
                      <TableHead>Gemeinschaft</TableHead>
                      <TableHead className="text-right">Aktionen</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {properties.map((property) => {
                      const thumbnailUrl = property.images?.[0] ? resolveApiAssetUrl(property.images[0]) : '';
                      const jointBusinessEditable = canEditJointBusiness(property);
                      const jointBusinessWritable = canUserToggleJointBusiness(user, property);
                      const jointBusinessChecked = isJointBusiness(property);
                      const isSavingJointBusiness = savingJointBusinessId === property.property_id;

                      return (
                        <TableRow key={property.property_id}>
                          <TableCell className="min-w-[320px]">
                            <div className="flex items-center gap-3">
                              <div className="h-14 w-20 shrink-0 overflow-hidden rounded-md border border-stone-200 bg-stone-100">
                                {thumbnailUrl ? (
                                  <img
                                    src={thumbnailUrl}
                                    alt={property.title}
                                    className="h-full w-full object-cover"
                                    loading="lazy"
                                  />
                                ) : (
                                  <div className="flex h-full w-full items-center justify-center text-stone-400">
                                    <Home className="h-5 w-5" />
                                  </div>
                                )}
                              </div>
                              <div className="min-w-0">
                                <div className="font-medium text-slate-900 line-clamp-2">{property.title}</div>
                                <div className="text-xs text-stone-500">{property.responsible_broker || 'Ohne Makler'}</div>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="secondary" className={property.property_type === 'haus' ? 'bg-blue-100 text-blue-700' : property.property_type === 'wohnung' ? 'bg-violet-100 text-violet-700' : 'bg-emerald-100 text-emerald-700'}>
                              {typeLabels[property.property_type] || property.property_type}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-stone-600">
                            {property.city || property.postcode || `${property.latitude.toFixed(4)}, ${property.longitude.toFixed(4)}`}
                          </TableCell>
                          <TableCell>{property.price ? `${property.price.toLocaleString('de-DE')} EUR` : '-'}</TableCell>
                          <TableCell className="text-stone-500 text-sm">{property.objektnr_extern || '-'}</TableCell>
                          <TableCell>
                            {jointBusinessEditable ? (
                              <div className="flex items-center gap-2">
                                <Switch
                                  data-testid={`joint-business-${property.property_id}`}
                                  checked={jointBusinessChecked}
                                  disabled={!jointBusinessWritable || isSavingJointBusiness}
                                  onCheckedChange={(checked) => handleJointBusinessChange(property, checked)}
                                  aria-label="Gemeinschaftsgeschäft"
                                />
                                <span className="text-sm text-stone-600">{jointBusinessChecked ? 'Ja' : 'Nein'}</span>
                              </div>
                            ) : (
                              <span className="text-sm text-stone-400">-</span>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            {canWriteAdmin ? (
                              <Button
                                data-testid={`delete-property-${property.property_id}`}
                                variant="ghost"
                                size="sm"
                                onClick={() => setDeleteDialog({ open: true, type: 'property', id: property.property_id, name: property.title })}
                                className="text-red-600 hover:bg-red-50"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            ) : (
                              <span className="text-sm text-stone-400">Lesen</span>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {properties.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8 text-stone-500">
                          Noch keine Immobilien importiert. Verwenden Sie den Upload-Tab, um Immobilien zu importieren.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="upload">
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="heading-md">Immobilienbestand aktualisieren</CardTitle>
                <CardDescription>Die Karte liest ausschließlich aus dem MR-properties-Ordner auf deinem Server.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-6">
                  <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                    <div>
                      <h3 className="heading-md text-slate-900 mb-2">Bestand aus MR properties neu einlesen</h3>
                      <p className="text-stone-600 max-w-2xl">
                        Der Server liest alle Objektordner, Bilder und Statusinformationen direkt aus MR properties.
                      </p>
                    </div>
                    <Button onClick={handleStoreSync} disabled={isSyncingStore || !canWriteAdmin} className="bg-emerald-700 hover:bg-emerald-800">
                      {isSyncingStore ? (
                        <>
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                          Synchronisiere...
                        </>
                      ) : (
                        <>
                          <RefreshCw className="w-4 h-4 mr-2" />
                          Jetzt synchronisieren
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="immostatus" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card className="border-0 shadow-lg">
                <CardContent className="p-5">
                  <p className="overline mb-2">Objekte im Verlauf</p>
                  <p className="text-3xl font-bold text-slate-900">{immoStatus.stats?.properties || 0}</p>
                </CardContent>
              </Card>
              <Card className="border-0 shadow-lg">
                <CardContent className="p-5">
                  <p className="overline mb-2">Offline-Ereignisse</p>
                  <p className="text-3xl font-bold text-amber-700">{immoStatus.stats?.offline_events || 0}</p>
                </CardContent>
              </Card>
              <Card className="border-0 shadow-lg">
                <CardContent className="p-5">
                  <p className="overline mb-2">Antworten</p>
                  <p className="text-3xl font-bold text-emerald-700">{immoStatus.stats?.feedback_submissions || 0}</p>
                </CardContent>
              </Card>
              <Card className="border-0 shadow-lg">
                <CardContent className="p-5">
                  <p className="overline mb-2">Preisänderungen</p>
                  <p className="text-3xl font-bold text-rose-700">{immoStatus.stats?.price_changes || 0}</p>
                </CardContent>
              </Card>
            </div>

            <Card className="border-0 shadow-lg overflow-hidden">
              <CardHeader className="bg-slate-900 text-white">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                  <div>
                    <CardTitle className="heading-md flex items-center gap-2">
                      <FileSpreadsheet className="w-5 h-5 text-amber-300" />
                      MR ImmoSTATUS
                    </CardTitle>
                    <CardDescription className="text-stone-300">
                      Objekt-Werdegang, Offline-Antworten und Preisänderungen nur für Admins.
                    </CardDescription>
                  </div>
                  <div className="flex flex-col gap-2 sm:flex-row">
                    <Button variant="outline" size="sm" onClick={fetchData} className="bg-white text-slate-900 hover:bg-stone-100">
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Aktualisieren
                    </Button>
                    <Button
                      size="sm"
                      onClick={handleDownloadImmoStatus}
                      disabled={!immoStatus.workbook?.exists}
                      className="bg-amber-600 hover:bg-amber-700 text-white"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Excel herunterladen
                    </Button>
                  </div>
                </div>
                <div className="mt-4 flex flex-wrap gap-3 text-sm text-stone-300">
                  <span className="inline-flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    Excel: {immoStatus.workbook?.updated_at ? formatDateTime(immoStatus.workbook.updated_at) : 'noch nicht erzeugt'}
                  </span>
                  <span>Stand: {formatDateTime(immoStatus.generated_at)}</span>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <div className="grid gap-3 border-b border-stone-200 bg-white p-4 md:grid-cols-4">
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger>
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Alle Status</SelectItem>
                      <SelectItem value="Online">Online</SelectItem>
                      <SelectItem value="Offline">Offline</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={feedbackFilter} onValueChange={setFeedbackFilter}>
                    <SelectTrigger>
                      <SelectValue placeholder="Rückmeldung" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Alle Rückmeldungen</SelectItem>
                      <SelectItem value="pending">Rückmeldung offen</SelectItem>
                      <SelectItem value="answered">Rückmeldung erhalten</SelectItem>
                      <SelectItem value="none">Ohne Rückmeldung</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={statusSort} onValueChange={setStatusSort}>
                    <SelectTrigger>
                      <SelectValue placeholder="Sortierung" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="latest">Neuester Verlauf</SelectItem>
                      <SelectItem value="offline">Zuletzt offline</SelectItem>
                      <SelectItem value="feedback">Neueste Antwort</SelectItem>
                      <SelectItem value="marketing_days">Vermarktungsdauer</SelectItem>
                      <SelectItem value="price_reductions">Preisreduzierungen</SelectItem>
                    </SelectContent>
                  </Select>
                  <div className="flex items-center justify-end text-sm text-stone-500">
                    {filteredImmoStatusProperties.length} von {(immoStatus.properties || []).length} Objekten
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Objekt</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Online</TableHead>
                        <TableHead>Offline</TableHead>
                        <TableHead>Antwort</TableHead>
                        <TableHead>Preis</TableHead>
                        <TableHead>Letzter Verlauf</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredImmoStatusProperties.map((property) => {
                        const latestEvent = property.events?.[0];
                        return (
                          <TableRow key={property.property_key}>
                            <TableCell className="min-w-[280px]">
                              <div className="font-semibold text-slate-900 line-clamp-2">{property.title || 'Objekt'}</div>
                              <div className="text-sm text-stone-500">
                                {[property.objektnr_extern, property.city, property.responsible_broker].filter(Boolean).join(' · ')}
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge className={property.current_status === 'Online' ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}>
                                {property.current_status}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-sm text-stone-600">{formatDateTime(property.last_online_at)}</TableCell>
                            <TableCell className="text-sm text-stone-600">{formatDateTime(property.last_offline_at)}</TableCell>
                            <TableCell className="text-sm text-stone-600">{formatDateTime(property.last_feedback_at)}</TableCell>
                            <TableCell>
                              <div className="space-y-1 text-sm text-stone-700">
                                <div className="flex items-center gap-2">
                                  <TrendingDown className="w-4 h-4 text-rose-600" />
                                  {property.price_reductions || 0} Reduzierung(en)
                                </div>
                                <div className="text-xs text-stone-500">{formatMonths(property.marketing_days)}</div>
                              </div>
                            </TableCell>
                            <TableCell className="min-w-[260px]">
                              {latestEvent ? (
                                <div className="space-y-2">
                                  <Badge variant="outline" className={eventTone[latestEvent.event_type] || 'bg-stone-100 text-stone-700'}>
                                    {latestEvent.event_label}
                                  </Badge>
                                  <div className="text-sm text-stone-600">{formatDateTime(latestEvent.occurred_at)}</div>
                                  {latestEvent.feedback && (
                                    <div className="text-sm text-stone-700 max-w-md">
                                      {formatFeedbackSummary(latestEvent.feedback)}
                                    </div>
                                  )}
                                  {property.latest_feedback && (
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => setSelectedFeedback({ property, feedback: property.latest_feedback })}
                                      className="h-8"
                                    >
                                      <Eye className="w-4 h-4 mr-2" />
                                      Antwort ansehen
                                    </Button>
                                  )}
                                </div>
                              ) : (
                                <span className="text-stone-400">-</span>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {filteredImmoStatusProperties.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={7} className="text-center py-10 text-stone-500">
                            Keine Objekte für die aktuelle Filterauswahl gefunden.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="heading-md">Aktueller Ereignisverlauf</CardTitle>
                <CardDescription>Chronologische Ansicht der letzten Status-, Feedback- und Preisereignisse.</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {(immoStatus.events || []).slice(0, 30).map((event) => (
                    <div key={event.event_id} className="grid gap-3 rounded-lg border border-stone-200 bg-white p-4 lg:grid-cols-[180px_220px_1fr]">
                      <div className="text-sm text-stone-500">{formatDateTime(event.occurred_at)}</div>
                      <div>
                        <Badge variant="outline" className={eventTone[event.event_type] || 'bg-stone-100 text-stone-700'}>
                          {event.event_label}
                        </Badge>
                      </div>
                      <div>
                        <div className="font-semibold text-slate-900">{event.title || event.objektnr_extern || event.property_id}</div>
                        <div className="text-sm text-stone-500">
                          {[event.objektnr_extern, event.city, event.responsible_broker].filter(Boolean).join(' · ')}
                        </div>
                        {event.event_type === 'price_change' && (
                          <div className="mt-2 text-sm text-rose-700">
                            {formatPrice(event.old_price)} ? {formatPrice(event.new_price)} · {event.note}
                          </div>
                        )}
                        {event.feedback && (
                          <div className="mt-2 rounded-md bg-stone-50 p-3 text-sm text-stone-700">
                            <strong>{formatFeedbackSummary(event.feedback)}</strong>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="brokerstats" className="space-y-6">
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                  <div>
                    <CardTitle className="heading-md">Makler-Statistik</CardTitle>
                    <CardDescription>Jahresauswertung für Huszak, Mahnke, Kissne-Ösi, Räther, Mäsel und Anwar.</CardDescription>
                  </div>
                  <div className="w-full sm:w-44">
                    <Select value={brokerYear} onValueChange={setBrokerYear}>
                      <SelectTrigger>
                        <SelectValue placeholder="Jahr" />
                      </SelectTrigger>
                      <SelectContent>
                        {immoStatusYears.map((year) => (
                          <SelectItem key={year} value={year}>{year}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid gap-4 lg:grid-cols-[1.4fr_1fr]">
                  <div className="h-80 rounded-lg border border-stone-200 bg-white p-4">
                    <p className="mb-4 font-semibold text-slate-900">Immobilienvolumen und Bestand</p>
                    <ResponsiveContainer width="100%" height="85%">
                      <BarChart data={brokerVolumeData}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis dataKey="name" />
                        <YAxis yAxisId="left" />
                        <YAxis yAxisId="right" orientation="right" />
                        <Tooltip formatter={(value, name) => name === 'volume' ? [`${formatNumber(value)} Tsd. EUR`, 'Volumen'] : [value, name === 'active' ? 'Aktiv' : 'Offline']} />
                        <Bar yAxisId="left" dataKey="volume" fill="#0f766e" radius={[4, 4, 0, 0]} />
                        <Bar yAxisId="right" dataKey="active" fill="#2563eb" radius={[4, 4, 0, 0]} />
                        <Bar yAxisId="right" dataKey="offline" fill="#b45309" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="h-80 overflow-y-auto rounded-lg border border-stone-200 bg-white p-4">
                    <p className="mb-4 font-semibold text-slate-900">Art der Immobilien nach Büro</p>
                    <div className="space-y-4">
                      {Object.entries(officeTypeData).map(([office, data]) => (
                        <div key={office} className="rounded-md border border-stone-200 p-3">
                          <div className="mb-2 flex items-center justify-between gap-3">
                            <strong className="text-sm text-slate-900">{office}</strong>
                            <Badge variant="secondary">Aktiv</Badge>
                          </div>
                          <div className="mb-3 flex flex-wrap gap-2 text-xs">
                            {Object.entries(data.active || {}).map(([type, count]) => (
                              <span key={type} className="rounded-full bg-emerald-50 px-2 py-1 text-emerald-800">
                                {typeLabels[type] || type}: {count}
                              </span>
                            ))}
                            {Object.keys(data.active || {}).length === 0 && <span className="text-stone-500">Keine aktiven Objekte</span>}
                          </div>
                          <div className="mb-2 flex items-center justify-between gap-3">
                            <strong className="text-sm text-slate-900">Archiviert</strong>
                          </div>
                          <div className="flex flex-wrap gap-2 text-xs">
                            {Object.entries(data.archived || {}).map(([type, count]) => (
                              <span key={type} className="rounded-full bg-amber-50 px-2 py-1 text-amber-800">
                                {typeLabels[type] || type}: {count}
                              </span>
                            ))}
                            {Object.keys(data.archived || {}).length === 0 && <span className="text-stone-500">Keine archivierten Objekte</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                  {selectedYearBrokerStats.map((broker, index) => (
                    <Card key={broker.brokerName} className="border border-stone-200 shadow-sm">
                      <CardHeader className="pb-3">
                        <div className="flex items-center justify-between gap-3">
                          <CardTitle className="text-lg">{broker.brokerName}</CardTitle>
                          <span className="h-3 w-3 rounded-full" style={{ backgroundColor: brokerColors[index % brokerColors.length] }} />
                        </div>
                        <CardDescription>{brokerYear}</CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="grid grid-cols-3 gap-2 text-center">
                          <div className="rounded-md bg-emerald-50 p-3">
                            <p className="text-xl font-bold text-emerald-800">{broker.active_properties || 0}</p>
                            <p className="text-xs text-emerald-700">Aktiv</p>
                          </div>
                          <div className="rounded-md bg-amber-50 p-3">
                            <p className="text-xl font-bold text-amber-800">{broker.offline_properties || 0}</p>
                            <p className="text-xs text-amber-700">Offline</p>
                          </div>
                          <div className="rounded-md bg-blue-50 p-3">
                            <p className="text-xl font-bold text-blue-800">{broker.feedback_submissions || 0}</p>
                            <p className="text-xs text-blue-700">Antworten</p>
                          </div>
                        </div>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between gap-3">
                            <span className="text-stone-500">Vermarktung Ø</span>
                            <strong>{broker.avg_marketing_days || 0} Tage</strong>
                          </div>
                          <div className="flex justify-between gap-3">
                            <span className="text-stone-500">Immobilienvolumen</span>
                            <strong>{formatPrice(broker.property_volume)}</strong>
                          </div>
                          <div className="flex justify-between gap-3">
                            <span className="text-stone-500">Umgesetzte Provision</span>
                            <strong>{formatPrice(broker.realized_commission)}</strong>
                          </div>
                          <div className="flex justify-between gap-3">
                            <span className="text-stone-500">Tippgeberprovision</span>
                            <strong>{formatPrice(broker.tipgeber_commission)}</strong>
                          </div>
                          <div className="flex justify-between gap-3">
                            <span className="text-stone-500">Provision Pienzenauer</span>
                            <strong>{formatPrice(broker.pienzenauer_commission)}</strong>
                          </div>
                          <div className="flex justify-between gap-3">
                            <span className="text-stone-500">Rückmeldung offen</span>
                            <strong>{broker.feedback_pending || 0}</strong>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {Object.entries(broker.feedback_reasons || {}).map(([reason, count]) => (
                            <Badge key={reason} variant="secondary" className="bg-stone-100 text-stone-700">
                              {reason}: {count}
                            </Badge>
                          ))}
                        </div>
                        <Button variant="outline" size="sm" className="w-full" onClick={() => { setSelectedBrokerStats(broker); setSelectedBrokerPropertyFilter('all'); }}>
                          <Eye className="mr-2 h-4 w-4" />
                          Details ansehen
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="directory">
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <CardTitle className="heading-md">Makler-Stammdaten</CardTitle>
                    <CardDescription>Diese Liste ist unabhängig von den Benutzerkonten pflegbar.</CardDescription>
                  </div>
                  <Button variant="outline" size="sm" onClick={fetchData}>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Aktualisieren
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {canWriteAdmin && (
                  <div className="grid gap-3 md:grid-cols-[1fr_auto]">
                    <Input value={newBrokerName} onChange={(event) => setNewBrokerName(event.target.value)} placeholder="Neuen Makler anlegen" />
                    <Button onClick={handleAddBroker} disabled={isSavingBroker} className="bg-slate-900 hover:bg-slate-800">
                      Makler hinzufügen
                    </Button>
                  </div>
                )}

                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Aktionen</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {brokerDirectory.map((broker) => (
                      <TableRow key={broker.broker_id}>
                        <TableCell>
                          <Input
                            value={editingBrokers[broker.broker_id]?.name || ''}
                            onChange={(event) => handleBrokerFieldChange(broker.broker_id, 'name', event.target.value)}
                            disabled={!canWriteAdmin}
                          />
                        </TableCell>
                        <TableCell>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => handleBrokerFieldChange(broker.broker_id, 'is_active', !editingBrokers[broker.broker_id]?.is_active)}
                            disabled={!canWriteAdmin}
                            className={editingBrokers[broker.broker_id]?.is_active ? 'text-emerald-700 border-emerald-300 hover:bg-emerald-50' : 'text-stone-600 border-stone-300 hover:bg-stone-50'}
                          >
                            {editingBrokers[broker.broker_id]?.is_active ? 'Aktiv' : 'Inaktiv'}
                          </Button>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            {canWriteAdmin ? (
                              <>
                                <Button variant="outline" size="sm" onClick={() => handleSaveBroker(broker.broker_id)} disabled={isSavingBroker}>
                                  Speichern
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setDeleteDialog({ open: true, type: 'directory', id: broker.broker_id, name: broker.name })}
                                  className="text-red-600 hover:bg-red-50"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </>
                            ) : (
                              <span className="text-sm text-stone-400">Lesen</span>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                    {brokerDirectory.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={3} className="text-center py-8 text-stone-500">
                          Noch keine Makler angelegt.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="users">
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="heading-md">Benutzerverwaltung</CardTitle>
                    <CardDescription>Benutzer für den Zugriff auf die Immobilienkarte freigeben oder sperren</CardDescription>
                  </div>
                  <Button variant="outline" size="sm" onClick={fetchData}>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Aktualisieren
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Benutzer</TableHead>
                      <TableHead>E-Mail</TableHead>
                      <TableHead>Rolle</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Aktionen</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {brokers.map((broker) => (
                      <TableRow key={broker.user_id}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            {broker.picture ? (
                              <img src={broker.picture} alt={broker.name} className="w-8 h-8 rounded-full" />
                            ) : (
                              <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center">
                                {broker.name?.charAt(0) || 'M'}
                              </div>
                            )}
                            <span className="font-medium">{broker.name}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-stone-600">{broker.email}</TableCell>
                        <TableCell>
                          <Badge variant={broker.role === 'admin' ? 'default' : 'secondary'}>{broker.role === 'admin' ? 'Admin' : 'Makler'}</Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant={broker.is_approved ? 'default' : 'destructive'} className={broker.is_approved ? 'bg-emerald-500' : ''}>
                            {broker.is_approved ? 'Freigegeben' : 'Ausstehend'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          {broker.role !== 'admin' && canWriteAdmin && (
                            <div className="flex items-center justify-end gap-2">
                              {broker.is_approved ? (
                                <Button
                                  data-testid={`revoke-${broker.user_id}`}
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleApproval(broker.user_id, false)}
                                  className="text-amber-600 border-amber-300 hover:bg-amber-50"
                                >
                                  <X className="w-4 h-4 mr-1" />
                                  Sperren
                                </Button>
                              ) : (
                                <Button
                                  data-testid={`approve-${broker.user_id}`}
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleApproval(broker.user_id, true)}
                                  className="text-emerald-600 border-emerald-300 hover:bg-emerald-50"
                                >
                                  <Check className="w-4 h-4 mr-1" />
                                  Freigeben
                                </Button>
                              )}
                              <Button
                                data-testid={`delete-broker-${broker.user_id}`}
                                variant="ghost"
                                size="sm"
                                onClick={() => setDeleteDialog({ open: true, type: 'user', id: broker.user_id, name: broker.name })}
                                className="text-red-600 hover:bg-red-50"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                    {brokers.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-8 text-stone-500">
                          Noch keine Benutzer registriert.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={Boolean(selectedFeedback)} onOpenChange={(open) => !open && setSelectedFeedback(null)}>
        <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Offline-Rückmeldung</DialogTitle>
            <DialogDescription>
              {selectedFeedback?.property?.objektnr_extern || selectedFeedback?.property?.property_id} · {selectedFeedback?.property?.title}
            </DialogDescription>
          </DialogHeader>
          {selectedFeedback?.feedback && (
            <div className="space-y-5">
              <div className="grid gap-3 md:grid-cols-2">
                <div className="rounded-lg bg-stone-50 p-4">
                  <p className="overline mb-1">Grund</p>
                  <p className="font-semibold text-slate-900">{selectedFeedback.feedback.reason_label || '-'}</p>
                </div>
                <div className="rounded-lg bg-stone-50 p-4">
                  <p className="overline mb-1">Abschlussart</p>
                  <p className="font-semibold text-slate-900">{selectedFeedback.feedback.deal_type_label || '-'}</p>
                </div>
                <div className="rounded-lg bg-stone-50 p-4">
                  <p className="overline mb-1">{selectedFeedback.feedback.deal_type === 'rent' ? 'Provision absolut' : 'Betrag'}</p>
                  <p className="font-semibold text-slate-900">{formatPrice(selectedFeedback.feedback.deal_amount)}</p>
                </div>
                {selectedFeedback.feedback.deal_type !== 'rent' && (
                  <>
                    <div className="rounded-lg bg-stone-50 p-4">
                      <p className="overline mb-1">Provision</p>
                      <p className="font-semibold text-slate-900">
                        {selectedFeedback.feedback.net_commission_percent ? `${selectedFeedback.feedback.net_commission_percent} % netto` : '-'}
                      </p>
                    </div>
                    <div className="rounded-lg bg-stone-50 p-4">
                      <p className="overline mb-1">Tippgeberprovision</p>
                      <p className="font-semibold text-slate-900">{formatPrice(selectedFeedback.feedback.tipgeber_commission_amount)}</p>
                    </div>
                  </>
                )}
                {selectedFeedback.feedback.strategic_new_id && (
                  <div className="rounded-lg bg-stone-50 p-4">
                    <p className="overline mb-1">Neue Objekt-ID</p>
                    <p className="font-semibold text-slate-900">{selectedFeedback.feedback.strategic_new_id}</p>
                  </div>
                )}
                <div className="rounded-lg bg-stone-50 p-4">
                  <p className="overline mb-1">Auftragsherkunft</p>
                  <p className="font-semibold text-slate-900">{selectedFeedback.feedback.lead_source || '-'}</p>
                </div>
                {selectedFeedback.feedback.joint_business && (
                  <div className="rounded-lg bg-stone-50 p-4">
                    <p className="overline mb-1">Gemeinschaftsgeschäft</p>
                    <p className="font-semibold text-slate-900">{selectedFeedback.feedback.joint_business === 'ja' ? 'Ja' : 'Nein'}</p>
                  </div>
                )}
              </div>

              {(selectedFeedback.feedback.lost_reason || selectedFeedback.feedback.paused_note || selectedFeedback.feedback.lead_source_note) && (
                <div className="rounded-lg border border-stone-200 p-4 text-sm text-stone-700">
                  {selectedFeedback.feedback.lost_reason && <p><strong>Verlustgrund:</strong> {selectedFeedback.feedback.lost_reason}</p>}
                  {selectedFeedback.feedback.paused_note && <p><strong>Pausierung:</strong> {selectedFeedback.feedback.paused_note}</p>}
                  {selectedFeedback.feedback.lead_source_note && <p><strong>Hinweis Herkunft:</strong> {selectedFeedback.feedback.lead_source_note}</p>}
                </div>
              )}

              <div>
                <h3 className="mb-3 flex items-center gap-2 font-semibold text-slate-900">
                  <FileText className="h-4 w-4" />
                  Dateien
                </h3>
                <div className="grid gap-3">
                  {(selectedFeedback.feedback.files || []).map((file) => (
                    <a
                      key={`${file.relative_path}-${file.original_name}`}
                      href={resolveApiAssetUrl(file.url)}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center justify-between gap-3 rounded-lg border border-stone-200 p-3 text-sm hover:bg-stone-50"
                    >
                      <span>
                        <strong className="block text-slate-900">{file.label || 'Datei'}</strong>
                        <span className="text-stone-500">{file.original_name || file.stored_name}</span>
                      </span>
                      <Download className="h-4 w-4 text-slate-500" />
                    </a>
                  ))}
                  {(!selectedFeedback.feedback.files || selectedFeedback.feedback.files.length === 0) && (
                    <p className="rounded-lg bg-stone-50 p-4 text-sm text-stone-500">Keine Dateien hinterlegt.</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(selectedBrokerStats)} onOpenChange={(open) => !open && setSelectedBrokerStats(null)}>
        <DialogContent className="max-h-[90vh] max-w-5xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedBrokerStats?.brokerName}</DialogTitle>
            <DialogDescription>
              {brokerYear} · {selectedBrokerStats?.properties?.length || 0} Objekte · Provision {formatPrice(selectedBrokerStats?.realized_commission)} · Pienzenauer {formatPrice(selectedBrokerStats?.pienzenauer_commission)}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 md:grid-cols-3">
            <button type="button" onClick={() => setSelectedBrokerPropertyFilter('all')} className={`rounded-lg border p-4 text-left ${selectedBrokerPropertyFilter === 'all' ? 'border-slate-900 bg-slate-50' : 'border-stone-200 bg-white'}`}>
              <p className="text-2xl font-bold text-slate-900">{selectedBrokerStats?.properties?.length || 0}</p>
              <p className="text-sm text-stone-500">Alle Objekte</p>
            </button>
            <button type="button" onClick={() => setSelectedBrokerPropertyFilter('active')} className={`rounded-lg border p-4 text-left ${selectedBrokerPropertyFilter === 'active' ? 'border-emerald-700 bg-emerald-50' : 'border-stone-200 bg-white'}`}>
              <p className="text-2xl font-bold text-emerald-800">{selectedBrokerStats?.active_properties || 0}</p>
              <p className="text-sm text-stone-500">Aktiv</p>
            </button>
            <button type="button" onClick={() => setSelectedBrokerPropertyFilter('archived')} className={`rounded-lg border p-4 text-left ${selectedBrokerPropertyFilter === 'archived' ? 'border-amber-700 bg-amber-50' : 'border-stone-200 bg-white'}`}>
              <p className="text-2xl font-bold text-amber-800">{selectedBrokerStats?.offline_properties || 0}</p>
              <p className="text-sm text-stone-500">Archiviert</p>
            </button>
          </div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Objekt</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Online</TableHead>
                  <TableHead>Offline</TableHead>
                  <TableHead>Dauer</TableHead>
                  <TableHead>Abschluss</TableHead>
                  <TableHead>Betrag</TableHead>
                  <TableHead>Provision</TableHead>
                  <TableHead>MR</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {selectedBrokerProperties.map((property) => {
                  const jointBusinessEditable = canEditJointBusiness(property);
                  const jointBusinessWritable = canUserToggleJointBusiness(user, property);
                  const jointBusinessChecked = isJointBusiness(property);
                  const isSavingJointBusiness = savingJointBusinessId === property.property_id;
                  const objectNumbers = property.duplicate_object_numbers?.length
                    ? property.duplicate_object_numbers.join(' / ')
                    : property.objektnr_extern;

                  return (
                    <TableRow key={property.property_key || property.objektnr_extern || property.title}>
                      <TableCell>
                        <div className="font-medium text-slate-900">{property.title || objectNumbers || '-'}</div>
                        <div className="text-sm text-stone-500">{[objectNumbers, property.city, property.responsible_broker || property.office].filter(Boolean).join(' · ')}</div>
                        {property.duplicate_object_numbers?.length > 1 && (
                          <div className="mt-1 text-xs font-medium text-amber-700">Zusammengeführte Statistik: {property.duplicate_object_numbers.join(' / ')}</div>
                        )}
                        {property.feedback_files?.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-2">
                            {property.feedback_files.map((file) => (
                              <a
                                key={`${file.relative_path}-${file.original_name}`}
                                href={resolveApiAssetUrl(file.url)}
                                target="_blank"
                                rel="noreferrer"
                                className="rounded-full bg-stone-100 px-2 py-1 text-xs text-slate-700 hover:bg-stone-200"
                              >
                                {file.label || file.original_name || 'Datei'}
                              </a>
                            ))}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>{property.current_status || '-'}</TableCell>
                      <TableCell>{formatDateTime(property.last_online_at)}</TableCell>
                      <TableCell>{formatDateTime(property.last_offline_at)}</TableCell>
                      <TableCell>{formatMonths(property.marketing_days)}</TableCell>
                      <TableCell>{property.deal_type_label || '-'}</TableCell>
                      <TableCell>{formatPrice(property.deal_amount)}</TableCell>
                      <TableCell>
                        <div>{formatPrice(property.realized_commission)}</div>
                        {property.deal_type !== 'rent' && property.net_commission_percent && <div className="text-xs text-stone-500">{property.net_commission_percent} % netto</div>}
                        {property.pienzenauer_commission ? <div className="text-xs text-stone-500">Pienzenauer {formatPrice(property.pienzenauer_commission)}</div> : null}
                      </TableCell>
                      <TableCell>
                        {jointBusinessEditable ? (
                          <div className="flex items-center gap-2">
                            <Switch
                              data-testid={`broker-stat-joint-business-${property.property_id}`}
                              checked={jointBusinessChecked}
                              disabled={!jointBusinessWritable || isSavingJointBusiness}
                              onCheckedChange={(checked) => handleJointBusinessChange(property, checked)}
                              aria-label="Gemeinschaftsgeschäft in MR"
                            />
                            <span className="whitespace-nowrap text-sm text-stone-600">{jointBusinessChecked ? 'M&R' : 'Eigene'}</span>
                          </div>
                        ) : (
                          <span className="text-sm text-stone-400">-</span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {selectedBrokerProperties.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} className="py-8 text-center text-stone-500">
                      Keine Objekte für dieses Jahr hinterlegt.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => !open && setDeleteDialog({ open: false, type: '', id: '', name: '' })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {deleteDialog.type === 'property' ? 'Immobilie löschen' : deleteDialog.type === 'directory' ? 'Makler löschen' : 'Benutzer löschen'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              Sind Sie sicher, dass Sie "{deleteDialog.name}" löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-btn"
              onClick={deleteDialog.type === 'property' ? handleDeleteProperty : deleteDialog.type === 'directory' ? handleDeleteBrokerDirectory : handleDeleteBrokerUser}
              className="bg-red-600 hover:bg-red-700"
            >
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
